import { AxiosError } from 'axios'
import { onlineManager } from '@tanstack/react-query'
import type { BayaWatchInstance, ErrorPayload } from '@bayawatch/sdk'

/**
 * Reports server failures (HTTP >= 500) and request timeouts from the shared
 * axios instance to BayaWatch.
 *
 * ## Why this exists
 *
 * The SDK only hooks `window` `error`, `unhandledrejection` and `console.error`
 * (`@bayawatch/sdk` 0.4.0, `ErrorHandler.install`). It does not wrap fetch or
 * XHR. A failed request that the UI handles — React Query turns the rejection
 * into an `isError` state — therefore reaches none of those hooks. The browser's
 * "Failed to load resource: the server responded with a status of 500" line is
 * printed by the network layer, not through `console.error`, so the console
 * patch never sees it either. On 2026-10-15 `GET /reports/{id}/document`
 * answered 500 three times on production, the session replay was recorded, and
 * no error group appeared.
 *
 * ## What is sent
 *
 * Method, URL PATH (no origin, no query string, no hash) and status or error
 * code. No request or response bodies, no headers.
 *
 * Path segments are redacted deny-by-default, because some routes carry a
 * bearer capability in the path: `/api/public/reports/{token}`,
 * `/api/public/issues/{token}` (both `bin2hex(random_bytes(32))`) and
 * `/reset_password/{token}`. Only lowercase resource names (letters, `_`, `-`,
 * `.`) and all-digit ids survive; any other segment becomes `:token`. The
 * message additionally collapses numeric ids to `:id`, so every report id
 * lands in one group, while the context keeps the numeric id for debugging.
 *
 * ## Double reporting
 *
 * If the same axios error later escapes as an unhandled rejection, or React
 * logs it via `console.error` from an error boundary, the SDK would capture it
 * a second time under axios's own message ("Request failed with status code
 * 500"). `filterBayaWatchPayload` is installed as the SDK's `beforeSend` and
 * drops automatic captures whose message AND stack equal those of an axios
 * error this module has just seen. It only drops errors we saw, so anything
 * this module skips (e.g. the maintenance 503) is reported exactly as before.
 *
 * ## Page URL
 *
 * The SDK stamps every payload — ours and its own automatic captures — with
 * `window.location.href`. The page routes `/reset_password/:token` and
 * `/public/{reports,issues}/:token` put the same secrets in the address bar,
 * so `filterBayaWatchPayload` also rewrites `payload.url` through the same
 * deny-by-default path redaction and drops the query string and hash.
 *
 * ## Volume
 *
 * `captureError` bypasses the SDK's own 100/minute limiter (that lives in its
 * window/console handler only), and every 10 buffered errors trigger an
 * ingest POST. An API outage with many mounted queries must not turn into an
 * ingest storm, so each grouping message is reported at most once per
 * `PER_MESSAGE_INTERVAL_MS`, and at most `MAX_REPORTS_PER_WINDOW` reports go
 * out per `RATE_WINDOW_MS` in total.
 */

/** Marks payloads captured by this module, so `beforeSend` lets them through. */
export const HTTP_FAILURE_SOURCE = 'http-client'

/** How long an axios error counts as "already seen" for dedupe. */
export const DUPLICATE_WINDOW_MS = 60_000

/** Minimum gap between two reports with the same grouping message. */
export const PER_MESSAGE_INTERVAL_MS = 10_000

/** Global cap: at most this many reports per `RATE_WINDOW_MS`. */
export const MAX_REPORTS_PER_WINDOW = 30
export const RATE_WINDOW_MS = 60_000

let client: Pick<BayaWatchInstance, 'captureError'> | null = null

/** `message::stack` of axios errors this module saw -> when. */
const recentlySeen = new Map<string, number>()

/** Grouping message -> when it was last sent. */
const lastSentByMessage = new Map<string, number>()

/** Send times inside the current rate window. */
let sentTimes: number[] = []

const dedupeKey = (message: string | undefined, stack: string | undefined): string =>
  `${message ?? ''}::${stack ?? ''}`

const forgetOlderThan = (map: Map<string, number>, now: number, maxAge: number): void => {
  for (const [key, at] of map) {
    if (now - at >= maxAge) map.delete(key)
  }
}

/** Returns true (and records the send) when a report may go out now. */
function takeRateBudget(message: string, now: number): boolean {
  forgetOlderThan(lastSentByMessage, now, PER_MESSAGE_INTERVAL_MS)
  const last = lastSentByMessage.get(message)
  if (last !== undefined && now - last < PER_MESSAGE_INTERVAL_MS) return false
  sentTimes = sentTimes.filter(at => now - at < RATE_WINDOW_MS)
  if (sentTimes.length >= MAX_REPORTS_PER_WINDOW) return false
  sentTimes.push(now)
  lastSentByMessage.set(message, now)
  return true
}

/** Set once the SDK has initialized; `null` disables reporting. */
export function setHttpFailureReporter(
  instance: Pick<BayaWatchInstance, 'captureError'> | null,
): void {
  client = instance
  recentlySeen.clear()
  lastSentByMessage.clear()
  sentTimes = []
}

/** A resource name such as `project_stages`, `offline-bundle` or `version.json`; the cap keeps a hex token that happens to have no digits out. */
const RESOURCE_SEGMENT = /^[a-z_.-]{1,40}$/
/**
 * A database id. Capped at 15 digits (every id we issue fits a JS safe
 * integer) so that a capability token which happens to be all digits — a
 * 64-character hex token or a 20-character reset token can be — is redacted
 * rather than kept as an id.
 */
const NUMERIC_ID_SEGMENT = /^\d{1,15}$/

/**
 * Path of a request URL without origin, query string or hash, with every
 * segment that is neither a resource name nor a numeric id replaced by
 * `:token`.
 */
export function requestPath(url: string | undefined): string {
  if (!url) return ''
  let path: string
  try {
    path = new URL(url, 'http://placeholder.invalid').pathname
  } catch {
    path = url.split(/[?#]/)[0]
  }
  const segments = path.split('/')
  return segments
    .map((segment, index) =>
      segment === '' ||
      (!isCapabilityPosition(segments, index) &&
        (RESOURCE_SEGMENT.test(segment) || NUMERIC_ID_SEGMENT.test(segment)))
        ? segment
        : ':token',
    )
    .join('/')
}

/**
 * True for the segment that holds the secret of a known capability route —
 * `reset_password/{token}`, `public/{issues,reports}/{token}` and the signup
 * campaign code (`signup/{code}`, `signup/links/{code}`), with or
 * without the `/api` prefix. Redacted by position because the shape rule above
 * is probabilistic: a random token can happen to look like a resource name
 * (all lowercase letters) or an id (all digits).
 */
function isCapabilityPosition(segments: string[], index: number): boolean {
  const parent = segments[index - 1]
  if (parent === 'reset_password') return true
  // Signup: the campaign code in `signup/{code}` (page) and `signup/links/{code}`
  // (API). `links` and `confirmations` are the static API segments after `signup`.
  if (parent === 'signup') return !SIGNUP_STATIC_SEGMENTS.has(segments[index])
  if (parent === 'links' && segments[index - 2] === 'signup') return true
  return (parent === 'issues' || parent === 'reports') && segments[index - 2] === 'public'
}

const SIGNUP_STATIC_SEGMENTS = new Set(['links', 'confirmations'])

/** Redacted path with numeric ids collapsed, used for grouping. */
export function groupingPath(path: string): string {
  return path
    .split('/')
    .map(segment => (NUMERIC_ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')
}

const isTimeout = (error: AxiosError): boolean =>
  error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'

/**
 * Report `error` if it is a server failure or a timeout. Never throws.
 *
 * Callers must have already excluded expected responses (the mock 202 from
 * the offline queue never arrives here as an error; the maintenance 503 is
 * filtered by the interceptor before this is called).
 */
export function reportHttpFailure(error: unknown): void {
  try {
    if (!client || !(error instanceof AxiosError)) return

    const status = error.response?.status
    const isServerError = status !== undefined && status >= 500
    // A timeout while offline is expected noise, not a server signal.
    // `onlineManager` rather than `navigator.onLine`, which lies in WKWebView;
    // main.tsx wires it to Capacitor's Network plugin on native.
    const isOnlineTimeout = !error.response && isTimeout(error) && onlineManager.isOnline()
    if (!isServerError && !isOnlineTimeout) return

    const method = (error.config?.method ?? 'get').toUpperCase()
    const path = requestPath(error.config?.url)
    const outcome = isServerError ? `HTTP ${status}` : 'HTTP timeout'
    const reported = new Error(`${outcome} ${method} ${groupingPath(path)}`)
    reported.name = 'HttpFailure'

    const now = Date.now()
    forgetOlderThan(recentlySeen, now, DUPLICATE_WINDOW_MS)
    // Recorded even when rate-limited below: a suppressed report must not
    // re-enter through the SDK's automatic capture of the same error.
    recentlySeen.set(dedupeKey(error.message, error.stack), now)
    if (!takeRateBudget(reported.message, now)) return

    client.captureError(reported, {
      source: HTTP_FAILURE_SOURCE,
      handled: true,
      http: {
        method,
        path,
        ...(isServerError ? { status } : { code: error.code }),
      },
    })
  } catch {
    // Monitoring must never break the request pipeline.
  }
}

/**
 * The page URL with origin and redacted path only — no query string or hash.
 * Returns '' when the URL cannot be parsed, rather than the raw value.
 */
export function sanitizePageUrl(href: string | undefined): string {
  if (!href) return ''
  try {
    const url = new URL(href)
    // `origin` is "null" for non-special schemes such as capacitor://.
    return `${url.protocol}//${url.host}${requestPath(url.pathname)}`
  } catch {
    return ''
  }
}

/**
 * SDK `beforeSend`, applied to every payload:
 * - drops an automatic capture (unhandled rejection, `console.error`,
 *   `window.onerror`) of an axios error this module already saw within
 *   `DUPLICATE_WINDOW_MS`;
 * - redacts the page URL the SDK stamps on the payload, and replaces that
 *   page's path secrets BY VALUE in every field (message, stack, context).
 */
export function filterBayaWatchPayload(
  payload: ErrorPayload,
  pageHref: string | undefined = payload.url,
): ErrorPayload | null {
  if (payload.context?.source !== HTTP_FAILURE_SOURCE) {
    const seenAt = recentlySeen.get(dedupeKey(payload.message, payload.stack))
    if (seenAt !== undefined && Date.now() - seenAt < DUPLICATE_WINDOW_MS) return null
  }
  // `pageHref` is the raw address of the page the payload was captured on,
  // whose secrets other fields might quote. It defaults to `payload.url`, which
  // is that address only when the SDK has no `sanitizeUrl`: with one, the SDK
  // redacts `url` before `beforeSend` runs, so `bayawatch.ts` passes the live
  // `location.href` instead.
  const secrets = pagePathSecrets(pageHref)
  return scrubSecrets({ ...payload, url: sanitizePageUrl(payload.url) }, secrets)
}

/** Shorter redacted segments are left alone: replacing them would mangle unrelated text. */
const MIN_SCRUBBED_SECRET_LENGTH = 8

/**
 * The path segments of a page URL that `requestPath` redacts, i.e. the
 * secrets a token route carries in the address bar.
 */
export function pagePathSecrets(href: string | undefined): string[] {
  if (!href) return []
  let pathname: string
  try {
    pathname = new URL(href).pathname
  } catch {
    return []
  }
  const kept = requestPath(pathname).split('/')
  return pathname
    .split('/')
    .filter(
      (segment, index) => kept[index] !== segment && segment.length >= MIN_SCRUBBED_SECRET_LENGTH,
    )
}

/**
 * Replaces every occurrence of `secrets` anywhere in the payload — message,
 * stack, context — with `:token`. The page URL is not the only field that can
 * quote it: an error message that includes `location.href`, or a context
 * object built from route params, would otherwise ship the token as is.
 * Returns `null` (drop the event) if the payload cannot be serialized.
 */
function scrubSecrets(payload: ErrorPayload, secrets: string[]): ErrorPayload | null {
  if (secrets.length === 0) return payload
  try {
    let json = JSON.stringify(payload)
    // Safe on the serialized form: a path segment holds no character that
    // JSON escapes, so it appears in the JSON exactly as in the URL.
    for (const secret of secrets) json = json.split(secret).join(':token')
    return JSON.parse(json) as ErrorPayload
  } catch {
    return null
  }
}
